import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import CopyButton from '../../components/CopyButton/CopyButton';

export function paymentLink(text) {
    try {
        const url = new URL(text);
        return ['https:', 'http:'].includes(url.protocol) ? url.href : null;
    } catch {
        return null;
    }
}

export function ProtectedPayoutFile({ apiUrl, payoutPk, kind, label }) {
    const { t } = useTranslation('payouts');
    const [opened, setOpened] = useState(false);
    const [asset, setAsset] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!opened) return undefined;
        const controller = new AbortController();
        let objectUrl;
        setAsset(null);
        setError(false);
        const load = async () => {
            try {
                const response = await fetch(`${apiUrl}providers/fiatpayoutrequest/${payoutPk}/${kind}/`, {
                    headers: { Authorization: `JWT ${localStorage.getItem('accessToken')}` },
                    signal: controller.signal,
                });
                if (!response.ok) throw new Error('file');
                const blob = await response.blob();
                if (controller.signal.aborted) return;
                objectUrl = URL.createObjectURL(blob);
                const extension = { 'application/pdf': 'pdf', 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp' }[blob.type] || 'bin';
                setAsset({ url: objectUrl, type: blob.type, filename: `${kind}-${payoutPk}.${extension}` });
            } catch {
                if (!controller.signal.aborted) setError(true);
            }
        };
        load();
        return () => {
            controller.abort();
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [opened, apiUrl, payoutPk, kind]);

    return (
        <div className="payout-protected-file">
            <button type="button" className="payout-action-btn" onClick={() => setOpened(!opened)}>{label}</button>
            {opened && error && <p role="alert">{t('qr.fileError')}</p>}
            {opened && !asset && !error && <p>{t('qr.loading')}</p>}
            {opened && asset && (
                <>
                    {['image/png', 'image/jpeg', 'image/webp'].includes(asset.type) && <img src={asset.url} alt={label} />}
                    {asset.type === 'application/pdf' && <iframe src={asset.url} title={label} />}
                    <a href={asset.url} download={asset.filename}>{t('qr.download')}</a>
                </>
            )}
        </div>
    );
}

export function QrPayoutDetails({ meta, apiUrl, payoutPk, showSalary = false }) {
    const { t, i18n } = useTranslation('payouts');
    const link = paymentLink(meta.qr_text);
    const deadline = meta.deadline_at ? new Date(meta.deadline_at) : null;
    return (
        <div className="payout-qr-details">
            <strong>{t('qr.method')}</strong>
            <span>{t('qr.projectFunds')}</span>
            {showSalary && <strong>{meta.amount} {meta.currency}</strong>}
            {showSalary && meta.salary !== null && <span>{t('qr.salary')}: {meta.salary} USDT</span>}
            {deadline && !Number.isNaN(deadline.getTime()) && <span>{t('qr.deadline', { date: deadline.toLocaleString(i18n.language === 'kz' ? 'kk' : i18n.language) })}</span>}
            {meta.is_overdue && <span className="payout-qr-overdue">{t('qr.overdue')}</span>}
            {meta.completed_late && <span className="payout-qr-overdue">{t('qr.completedLate')}</span>}
            {meta.qr_text && (
                <div className="payout-qr-text">
                    <span>{meta.qr_text}</span>
                    <CopyButton text={meta.qr_text} />
                </div>
            )}
            {link && <a href={link} target="_blank" rel="noopener noreferrer">{t('qr.openPayment')}</a>}
            {meta.has_qr_file && <ProtectedPayoutFile apiUrl={apiUrl} payoutPk={payoutPk} kind="qr_file" label={t('qr.viewQr')} />}
            {meta.has_receipt && <ProtectedPayoutFile apiUrl={apiUrl} payoutPk={payoutPk} kind="receipt_file" label={t('qr.viewReceipt')} />}
        </div>
    );
}

export function QrPayoutModal({ apiUrl, payoutPk, payoutId, meta, onClose, onCompleted }) {
    const { t } = useTranslation('payouts');
    const [isOpen, setIsOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const titleId = `qr-payout-title-${payoutPk}`;
    const helpId = `qr-payout-help-${payoutPk}`;
    // Open after mounting so react-modal registers once during StrictMode's
    // initial mount replay and reliably restores the app's aria-hidden state.
    useEffect(() => { setIsOpen(true); }, []);
    const submit = async () => {
        if (!file || busy) return;
        setBusy(true);
        setError('');
        try {
            const body = new FormData();
            body.append('receipt_file', file);
            const response = await fetch(`${apiUrl}providers/fiatpayoutrequest/${payoutPk}/upload_receipt/`, {
                method: 'POST',
                headers: { Authorization: `JWT ${localStorage.getItem('accessToken')}` },
                body,
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.text || t('completeModal.uploadError'));
            onCompleted();
        } catch (err) {
            setError(err.message || t('completeModal.uploadError'));
        } finally {
            setBusy(false);
        }
    };
    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={() => { if (!busy) onClose(); }}
            shouldCloseOnEsc={!busy}
            shouldCloseOnOverlayClick={!busy}
            className="confirmation-modal large-modal payout-qr-modal"
            overlayClassName="confirmation-modal-overlay payout-qr-modal-overlay"
            portalClassName="ReactModalPortal payout-qr-portal assistant-400"
            contentLabel={t('qr.uploadTitle')}
            aria={{ labelledby: titleId, describedby: helpId }}
        >
            <div className="confirmation-modal-header">
                <h2 id={titleId} className="confirmation-modal-title">{t('completeModal.titleWithId', { id: payoutId })}</h2>
                <button type="button" aria-label={t('common:close')} className="confirmation-modal-close" onClick={onClose} disabled={busy}>×</button>
            </div>
            <div className="confirmation-modal-content">
                <QrPayoutDetails meta={meta} apiUrl={apiUrl} payoutPk={payoutPk} showSalary />
                <p id={helpId}>{t('qr.uploadHelp')}</p>
                <input type="file" aria-label={t('qr.uploadTitle')} accept=".pdf,.png,.jpg,.jpeg,.webp" disabled={busy} onChange={(event) => { setFile(event.target.files[0]); setError(''); }} />
                {error && <p role="alert" className="payout-qr-error">{error}</p>}
            </div>
            <div className="confirmation-modal-actions">
                <button className="confirmation-modal-button confirm" onClick={submit} disabled={!file || busy}>{busy ? t('completeModal.uploading') : t('qr.uploadComplete')}</button>
                <button className="confirmation-modal-button cancel" onClick={onClose} disabled={busy}>{t('common:close')}</button>
            </div>
        </Modal>
    );
}
